<script setup lang="ts">
import CurrencyText from '@uiKit/components/CurrencyText.vue'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import PiIcon from '@uiKit/elements/PiIcon.vue'
import { formatAed } from '@shared/utils'
import { PLACING_STEPS } from '../constants/investFlow'

// Placing Order (Figma 32590:67999): a spinning wallet mark, "Authorizing",
// and a checklist that ticks through while the order is being placed.

interface Props {
  /** Index of the step in progress; PLACING_STEPS.length once all are done. */
  activeStep: number
  totalCommitted: number
}

defineProps<Props>()
</script>

<template>
  <Box
    direction="col"
    align="center"
    justify="center"
    gap="32px"
    class="placing"
    role="status"
    aria-live="polite"
  >
    <Box direction="col" align="center" gap="6">
      <Box align="center" justify="center" class="placing__mark">
        <Box class="placing__ring" aria-hidden="true" />
        <PiIcon name="wallet" class="placing__wallet" />
      </Box>
      <Box direction="col" align="center" gap="10px">
        <Text type="display" weight="semibold" is="h3">Authorizing</Text>
        <Text type="body" weight="medium" color="muted" align="center"
          >Please do not close the app</Text
        >
      </Box>
    </Box>

    <Box is="ol" direction="col" gap="4" class="placing__steps">
      <Box v-for="(label, index) in PLACING_STEPS" :key="label" is="li" align="center" gap="3">
        <Box v-if="index < activeStep" align="center" justify="center" class="placing__done"
          ><PiIcon name="check" class="placing__done-icon"
        /></Box>
        <Box v-else-if="index === activeStep" class="placing__spinner" aria-hidden="true" />
        <Box v-else class="placing__pending" aria-hidden="true" />
        <Text
          type="body"
          :weight="index === activeStep ? 'semibold' : 'medium'"
          :color="
            index === activeStep
              ? 'primary'
              : index < activeStep
                ? 'default'
                : 'var(--p-surface-400)'
          "
          is="span"
        >
          {{ label }}
        </Text>
      </Box>
    </Box>

    <Box direction="col" align="center" gap="1">
      <Text type="display" weight="bold" is="span"
        ><CurrencyText :text="formatAed(totalCommitted)"
      /></Text>
      <Text type="body" weight="bold" color="var(--p-surface-400)" is="span">TOTAL COMMITTED</Text>
    </Box>
  </Box>
</template>

<style scoped>
.placing {
  flex: 1;
  padding: 2rem 1.5rem;
}

.placing__mark {
  position: relative;
  width: 64px;
  height: 64px;
}

.placing__ring {
  position: absolute;
  inset: 0;
  border: 4px solid var(--p-primary-100);
  border-left-color: var(--p-primary-600);
  border-bottom-color: var(--p-primary-600);
  border-radius: 50%;
  animation: placing-spin 1.1s linear infinite;
}

.placing__wallet {
  font-size: 22px;
  color: var(--p-primary-600);
}

.placing__steps {
  width: 283px;
  max-width: 100%;
  margin: 0;
  padding: 20px;
  list-style: none;
  border: 1px solid var(--p-surface-100);
  border-radius: 20px;
  background: var(--p-surface-0);
  filter: drop-shadow(0 8px 15px rgba(0, 0, 0, 0.04));
}

.placing__done {
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: var(--p-success-500);
}

.placing__done-icon {
  font-size: 9px;
  color: var(--p-surface-0);
}

.placing__spinner,
.placing__pending {
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  border-radius: 50%;
}

.placing__spinner {
  border: 2px solid var(--p-primary-100);
  border-top-color: var(--p-primary-600);
  border-left-color: var(--p-primary-600);
  animation: placing-spin 0.9s linear infinite;
}

.placing__pending {
  border: 1.5px solid var(--p-surface-200);
}

@keyframes placing-spin {
  to {
    transform: rotate(360deg);
  }
}

@media (prefers-reduced-motion: reduce) {
  .placing__ring,
  .placing__spinner {
    animation-duration: 3s;
  }
}
</style>
